import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import AuthBrand from "../auth/AuthBrand";
import { BRAND_NAME } from "../../config/brand";

const AuthLayout: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname.split("/").pop();
  const marketingTitles: Record<string, string> = {
    login: "Smarter property management starts here",
    register: "All-in-one platform for modern property teams",
    "forgot-password": "We’ll help you get back in",
    "reset-password": "Secure your account with a fresh start",
    "verify-email": `Just one step left to unlock ${BRAND_NAME}`,
    "verify-phone": "Verify your phone to get WhatsApp updates",
  };
  const marketingDesc: Record<string, string> = {
    login:
      "Access real-time analytics, maintenance workflows, tenant communication, and more — all from a unified dashboard.",
    register:
      "Streamline onboarding, automate maintenance, and deliver better resident experiences with powerful built‑in tools.",
    "forgot-password":
      "No worries — enter your email and we’ll send instructions to reset your password securely.",
    "reset-password":
      "Create a strong password to keep your account protected and your data secure.",
    "verify-email":
      "We’re validating your account so you can start exploring powerful automation & collaboration features.",
    "verify-phone":
      "Confirm your number to receive important updates and reminders on WhatsApp.",
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-secondary-50 via-white to-primary-50 dark:from-gray-950 dark:via-gray-900 dark:to-primary-950 relative overflow-hidden">
      {/* Decorative gradients */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-400/20 dark:bg-primary-600/15 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-24 w-[500px] h-[500px] bg-purple-400/10 dark:bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-72 h-72 bg-fuchsia-400/10 dark:bg-fuchsia-600/10 rounded-full blur-3xl" />
      </div>

      {/* Marketing / Side Panel (aligned to auth card center while keeping brand + footer) */}
      <aside className="hidden md:flex relative flex-col flex-1 pl-16 pr-14 xl:pl-24 xl:pr-20 pt-14 pb-10">
        <div>
          <AuthBrand />
        </div>
        <div className="flex-1 flex justify-center items-center">
          <div className="max-w-md pt-6">
            <h2 className="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white leading-tight">
              {marketingTitles[pathname || "login"] || `Welcome to ${BRAND_NAME}`}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-gray-600 dark:text-gray-300">
              {marketingDesc[pathname || "login"] || marketingDesc.login}
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1">
                <span className="size-2 rounded-full bg-primary-500" />{" "}
                Real-time updates
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="size-2 rounded-full bg-purple-500" /> Secure by
                design
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="size-2 rounded-full bg-fuchsia-500" /> Built
                for teams
              </span>
            </div>
          </div>
        </div>
        <p className="pt-14 text-xs text-gray-500 dark:text-gray-500">
          © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
        </p>
      </aside>

      {/* Auth Outlet */}
      <main className="relative flex-1 flex items-center justify-center pt-10 pb-10 px-6 sm:px-10">
        <div className="w-full max-w-md mx-auto">
          <div className="md:hidden mb-8 flex justify-center">
            <AuthBrand compact />
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AuthLayout;
